import React, { useEffect, useRef, useState, useMemo } from "react";

import { clsx } from "clsx";
import { useLocation } from "@reach/router";

import {
  Link,
  useNavStack,
  HeaderStack,
  NavOverlay,
  NavConfigProvider,
  Ticker,
  SanityLink,
  IconAvatar,
  HeaderPriorityBanner,
  type HeaderStackProps,
} from "@bared/ui";

import type { NavigationMenuViewedEvent } from "types/events";

import { useHref } from "~/hooks/useHref";
import { useSite } from "~/hooks/useSite";
import { useSearch } from "~/hooks/useSearch";
import { useLiveChat } from "~/hooks/useLiveChat";
import { useWishlistCount } from "~/hooks/useWishlist";
import { useSessionCustomer, useSessionLoggedIn } from "~/hooks/useSession";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";
import { useCartCount, useCartToggle } from "~/hooks/useCart";
import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

import { AnalyticsViewItemListProvider } from "~/providers/AnalyticsViewItemListProvider";

import { ProductCard } from "~/components/ProductCard";
import { CountdownTimer } from "~/components/CountdownTimer";

import { TestDrives } from "./TestDrives";

export interface HeaderProps {
  /** Gender used to calculate root menu */
  gender?: string | null;
  /** Should the header be transparent */
  transparent?: boolean;
}

export function Header({ gender, transparent }: HeaderProps) {
  const site = useSite();
  const location = useLocation();

  const { settingPriorityBanner } = useRouteLoaderData("root");

  const { announcements, navConfig } = useRouteLoaderData("routes/$site");

  const cartCount = useCartCount();
  const wishlistCount = useWishlistCount();

  const customer = useSessionCustomer();

  const { openCart } = useCartToggle();

  const searchFormAction = useHref("/search");

  const [searchQuery, setSearchQuery] = useState<string>("");

  const search = useSearch({
    searchQuery,
    productLimit: 4,
    collectionLimit: 8,
  });

  // Reset the search when location changes.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("resetSearch"));
  }, [location]);

  if (!navConfig) return null;

  /**
   * Determine if the viewport is bigger than the md breakpoint.
   * This is purposely not reactive i.e. you need to refresh the page to change it.
   */
  const isDesktop =
    "undefined" !== typeof window &&
    768 <= window.innerWidth &&
    !window.matchMedia("(pointer: coarse)").matches;

  const leftContent = [
    <div className="flex items-center justify-start text-caption text-white md:pl-zero">
      <div
        className="hidden h-full content-center border-r-[1px] border-white border-opacity-30 pr-mini md:block"
        aria-label={`Currency: ${site.currencyCode}`}
        role="region"
        tabIndex={0}
      >
        {site.currencyCode}
      </div>
      <div
        role="region"
        aria-label={`Site announcements: ${announcements.map((item) => item.title).join(", ")}`}
        tabIndex={0}
      >
        <Ticker className="!justify-start pl-zero md:pl-mini">
          {announcements.map((item) => (
            <div key={item.title} tabIndex={-1} aria-hidden="true">
              {item.textLink ? (
                <SanityLink
                  className="text-caption text-white"
                  data={item.textLink}
                  target={item.textLink._type === "link" ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  tabIndex={-1}
                >
                  {item.title}
                </SanityLink>
              ) : (
                <span className="text-caption text-white" tabIndex={-1}>
                  {item.title}
                </span>
              )}
            </div>
          ))}
        </Ticker>
      </div>
    </div>,
  ];

  const priorityBanner = settingPriorityBanner?.enabled ? (
    <HeaderPriorityBanner
      text={settingPriorityBanner.text}
      additionalLink={settingPriorityBanner.additionalLink}
      priorityLevel={settingPriorityBanner.priorityLevel}
      className="z-[51] w-full transition-all duration-300"
      reappearAfter={settingPriorityBanner.reappearAfter}
    />
  ) : false === customer?.completedAccount &&
    !location.pathname.endsWith("/complete-account") ? (
    <HeaderPriorityBanner
      text={"Stay in the loop on new arrivals, promos and more."}
      additionalLink={{
        title: "Complete your profile",
        link: "/complete-account",
      }}
      priorityLevel={"low"}
      className="z-[51] w-full transition-all duration-300"
      reappearAfter={30}
    />
  ) : null;

  const contactLink =
    "au" === site.handle
      ? "https://au.support.baredfootwear.com/hc/en-au/p/contact"
      : "nz" === site.handle
        ? "https://nz.support.baredfootwear.com/hc/en-nz/p/contact"
        : "https://us.support.baredfootwear.com/hc/en-us/p/contact";

  const rightContent = [
    <div className="flex items-center justify-end gap-mini text-caption text-white">
      <TestDrives />
      <AccountLink />
      <Link href={contactLink} className="hidden md:flex">
        Contact Us
      </Link>
      <Link href="/store-locations" className="hidden md:flex">
        Stores
      </Link>
    </div>,
  ];

  const searchResults = useMemo<HeaderStackProps["searchResults"]>(
    () => ({
      searchQuery: search.data?.searchQuery ?? "",
      collectionResults: (search.data?.collections ?? []).map((collection) => ({
        label: collection.title,
        href: `/collections/${collection.handle}`,
      })),
      productResults: (search.data?.products ?? []).map((props, index) => (
        <ProductCard
          {...props}
          index={index}
          key={props.handle}
          sizes="50vw"
          enableSheet={false}
          showColourCount={true}
        />
      )),
      helpLinks: [
        { href: "/returns-and-exchanges", label: "Returns & Exchanges" },
        { href: "/shipping-delivery", label: "Shipping & Delivery" },
        { href: "/fit-and-sizing", label: "Fit & Sizing" },
        { href: "/account", label: "Order & Account" },
      ],
    }),
    [search],
  );

  // Calculate a root menu based on the gender.
  const rootMenus = (() => {
    /**
     * These were used to open a gender specific menu on particular pages.
     * They have been disabled such that all pages will now link to the root
     * menu when opened.
     * @see https://bared-melbourne.slack.com/archives/C045GT6BNAU/p1754963505691709?thread_ts=1754872574.240029&cid=C045GT6BNAU
     */
    // if ("women" === gender) return ["root", "women"];
    // if ("men" === gender) return ["root", "men"];
    return ["root"];
  })();

  return (
    <NavConfigProvider config={navConfig} rootMenus={rootMenus}>
      <AnalyticsViewItemListProvider
        itemListId="instant-search"
        itemListName="Instant Search"
      >
        <div className="fixed left-zero right-zero top-zero z-50">
          <HeaderStack
            isDesktop={isDesktop}
            transparent={transparent}
            onToggleCart={openCart}
            leftContent={leftContent}
            cartCount={cartCount ?? 0}
            rightContent={rightContent}
            wishlistCount={wishlistCount ?? 0}
            priorityBanner={priorityBanner}
            countdownTimer={<CountdownTimer />}
            onSearchQuery={setSearchQuery}
            searchFormAction={searchFormAction}
            searchResults={searchResults}
          />
        </div>
      </AnalyticsViewItemListProvider>
      {!isDesktop && <NavOverlay />}
      <LiveChatToggler />
      <AnalyticsSubscriber />
    </NavConfigProvider>
  );
}

/**
 * Listens for changes to the nav stack and opens or closes the live chat
 * widget to prevent it covering up menus.
 */
function LiveChatToggler() {
  const navStack = useNavStack();
  const liveChat = useLiveChat();
  const [wasMenuOpen, setWasMenuOpened] = useState(false);

  useEffect(() => {
    if (navStack.length > 0) {
      liveChat.hide();
      setWasMenuOpened(true);
    } else if (wasMenuOpen) {
      liveChat.show();
      setWasMenuOpened(false);
    }
  }, [navStack, liveChat, wasMenuOpen]);

  return null;
}

/**
 * Listens for changes to the nav stack and emits analytics events.
 */
function AnalyticsSubscriber() {
  const navStack = useNavStack();
  const googleTagManager = useGoogleTagManager();

  const menuPathRef = useRef<string>("");

  useEffect(() => {
    // No menus open, do nothing.
    if (0 === navStack.length) return;

    /**
     * Compute and check a referentially equal string so we don't trigger events
     * for the same path over and over.
     */
    const menuPath = navStack.join(".");
    if (menuPathRef.current === menuPath) return;
    menuPathRef.current = menuPath;

    // Create an event.
    const event: NavigationMenuViewedEvent = {
      event: "Navigation Menu Viewed",
      properties: {
        menu_path: navStack,
        menu_depth: navStack.length,
        menu_id: navStack[navStack.length - 1],
      },
    };

    // Send to Google Tag Manager.
    googleTagManager.push(event);
  }, [navStack, googleTagManager]);

  return null;
}

function AccountLink({ className }: { className?: string }) {
  const loggedIn = useSessionLoggedIn();

  if (true === loggedIn) {
    return (
      <>
        <Link
          href="https://account.baredfootwear.com"
          className={clsx(
            "gap-1 flex shrink-0 items-center font-medium",
            className,
          )}
        >
          <IconAvatar className="w-mini" />
          My Account
        </Link>
      </>
    );
  }

  if (false === loggedIn) {
    return (
      <>
        <Link
          external
          href="/api/auth/redirect"
          className={clsx(
            "gap-1 flex shrink-0 items-center font-medium",
            className,
          )}
        >
          <IconAvatar className="w-mini" />
          Login
        </Link>
      </>
    );
  }

  return null;
}
